import { Box } from '@mui/material';
import { Dialog } from '@components/dialog';
import { useForm } from 'react-hook-form';
import { Form } from '@components/forms/Form';
import { FormSection } from '@components/forms/FormSection';
import { FormFieldType, TFormField } from '@models/forms/formFields.type';
import { useNewTask } from '@services/underwritingTasks/useNewTask';
import { useParams } from 'react-router-dom';
import { UnderwritingRulesGetResponse } from '@lio/api-types';

interface AddUWIssueDialogProps {
  open: boolean;
  onClose: () => void;
  underwritingRuleItems: UnderwritingRulesGetResponse['items'];
}

export const AddUWIssueDialog = ({ open, onClose, underwritingRuleItems }: AddUWIssueDialogProps) => {
  const { quoteId } = useParams<{ quoteId: string }>();
  const { createNewTaskAsync, isPending: isCreating } = useNewTask(quoteId);

  const getAddIssueFields = (
    underwritingRuleItems: UnderwritingRulesGetResponse['items'],
  ): { [key: string]: TFormField } => ({
    condition: {
      name: 'condition',
      fieldType: FormFieldType.SelectField,
      label: 'Condition',
      required: true,
      gridWidth: { xs: 12 },
      fieldConfig: {
        selectField: {
          options: underwritingRuleItems?.map((rule) => ({ label: rule.underwritingLabel, value: rule.id })) || [],
        },
      },
    },
    reasoning: {
      name: 'reasoning',
      fieldType: FormFieldType.TextField,
      label: "Underwriter's Reasoning",
      required: true,
      gridWidth: { xs: 12 },
      fieldConfig: {
        textField: {
          multiline: true,
          rows: 3,
        },
      },
      placeholder: 'Use this space to explain why this issue should or should not be overridden',
    },
  });

  const addIssueForm = useForm({
    mode: 'onChange',
    defaultValues: { condition: '', reasoning: '' },
  });

  const addIssueFields = getAddIssueFields(underwritingRuleItems);
  const isAddIssueFormValid = addIssueForm.formState.isValid;

  const handleAddIssueFormSubmit = async (data: { condition: string; reasoning: string }) => {
    await createNewTaskAsync({
      underwritingRuleId: data.condition,
      underwritingRuleDetail: '',
      underwriterComment: data.reasoning,
    });
    onClose();
    addIssueForm.reset();
  };

  return (
    <Dialog
      open={open}
      handleCloseDialog={onClose}
      title='Add New Underwriting Issue'
      primaryBtnText='Create'
      primaryBtnAction={addIssueForm.handleSubmit(handleAddIssueFormSubmit)}
      isPrimaryBtnLoading={isCreating}
      secondaryBtnText='Cancel'
      secondaryBtnAction={onClose}
      isPrimaryBtnDisabled={!isAddIssueFormValid}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', paddingX: 2 }}>
        <Form formId='add-uw-issue-form' methods={addIssueForm}>
          <FormSection fields={addIssueFields} />
        </Form>
      </Box>
    </Dialog>
  );
};
